import {useMutation} from "@apollo/client";
import {Button, Textarea} from "@mantine/core";
import {Formik, FormikErrors, setIn} from "formik";
import {noop} from "lodash";
import React, {useState} from "react";

import classes from "./WriteReview.module.scss";
import * as mutations from "../../graphql/mutations";
import type {
  FormError,
  Review as ReviewType,
  ReviewInput,
} from "../../graphql/types";
import {useAuth} from "../contexts/AuthContext";
import ErrorAlert from "../ErrorAlert";
import {showSignIn} from "../modals/CombinedSignIn/CombinedSignIn";
import StarRating from "../StarRating";

export interface WriteReviewProps {
  reviewInput: ReviewInput;
  onCreate?(review: ReviewType): void;
  onCancel?(): void;
}

export default function WriteReview({
  reviewInput,
  onCancel,
  onCreate = noop,
}: WriteReviewProps) {
  const {authentication} = useAuth();
  const [error, setError] = useState<string | null>(null);

  const [createReview, {loading: creatingReview}] = useMutation(
    mutations.CREATE_REVIEW
  );

  const isSignedIn = authentication.status === "SIGNED_IN";

  return (
    <div className={classes.writeReview}>
      <Formik<Partial<ReviewInput>>
        initialValues={reviewInput}
        validate={values => {
          let errors: FormikErrors<Partial<ReviewInput>> = {};

          if (!values.rating && !values.comment) {
            errors = setIn(
              errors,
              "comment",
              "Either a rating or a comment is required"
            );
          }

          return errors;
        }}
        onSubmit={async (values, {setSubmitting, setErrors}) => {
          setError(null);

          try {
            const {data: {entities: {review = undefined} = {}} = {}} =
              await createReview({variables: {input: values}});

            if (review.__typename == "FormError") {
              const formError = review as FormError;
              if (!formError.field || formError.field === "captchaResponse") {
                // General error
                if (formError?.message) {
                  setError(formError.message);
                }
              } else {
                // Field-specific error
                setErrors({[formError.field]: formError.message});
              }

              return;
            }

            onCreate(review);
          } catch (ex: any) {
            setError(ex.message);
          } finally {
            setSubmitting(false);
          }
        }}>
        {({
          handleSubmit,
          handleChange,
          values,
          errors,
          isValidating,
          isSubmitting,
          submitCount,
          setFieldValue,
        }) => (
          <form onSubmit={handleSubmit} className={classes.writeReviewForm}>
            <ErrorAlert message={error?.toString()} />

            <StarRating
              rating={values.rating || 0}
              onChange={rating => setFieldValue("rating", rating)}
              readOnly={!isSignedIn}
            />

            <Textarea
              name="comment"
              placeholder="Write your review..."
              disabled={!isSignedIn}
              required
              error={submitCount > 0 && errors.comment}
              value={values.comment || ""}
              onChange={handleChange}
            />

            {isSignedIn ? (
              <Button.Group>
                <Button
                  variant="primary"
                  size="xs"
                  onClick={() => handleSubmit()}
                  disabled={isValidating || isSubmitting}
                  loading={isSubmitting || creatingReview}>
                  Post review
                </Button>
                {onCancel && (
                  <Button
                    variant="default"
                    size="xs"
                    onClick={() => onCancel()}
                    disabled={isValidating || isSubmitting}>
                    Cancel
                  </Button>
                )}
              </Button.Group>
            ) : (
              <div className={classes.signInWarning}>
                Please{" "}
                <a
                  href="/signIn"
                  onClick={event => {
                    event.preventDefault();
                    showSignIn("You must be signed in to write reviews.");
                  }}>
                  sign in
                </a>{" "}
                to write a review.
              </div>
            )}
          </form>
        )}
      </Formik>
    </div>
  );
}
